import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { onValue, ref, set } from 'firebase/database';
import { database } from '../firebase/firebase';
import { useAuth } from './useAuth';

/**
 * Settings that change how the app behaves for one person.
 *
 * They live on the account (`users/<uid>/preferences`) rather than in
 * localStorage, so a setting made on a laptop is already in place on the phone.
 * That makes them a signed-in feature: a guest always gets the defaults.
 */
export interface UserPreferences {
    /** Put "split between" at the top of the expense form. Some people decide who
        shares a bill before they look at what it cost, and the picker sat at the
        very bottom, below four fields they had to scroll past. */
    splitFirst: boolean;
}

const DEFAULT_PREFERENCES: UserPreferences = {
    splitFirst: false,
};

interface UserPreferencesContextType {
    preferences: UserPreferences;
    /** False for a guest: there is no account to keep the setting on. */
    canEdit: boolean;
    updatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType>({
    preferences: DEFAULT_PREFERENCES,
    canEdit: false,
    updatePreference: async () => {},
});

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const uid = user?.uid;
    const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);

    // Subscribed at the app root so the value is already here by the time a form
    // opens, instead of the fields rearranging themselves under the first tap.
    useEffect(() => {
        if (!uid) {
            setPreferences(DEFAULT_PREFERENCES);
            return;
        }

        return onValue(
            ref(database, `users/${uid}/preferences`),
            (snapshot) => {
                setPreferences({ ...DEFAULT_PREFERENCES, ...(snapshot.val() || {}) });
            },
            (error) => {
                console.error('Error loading user preferences:', error);
            }
        );
    }, [uid]);

    // No local copy to update: the listener above hears its own write straight
    // away, and hears the rollback too if the write is rejected.
    const updatePreference = useCallback(async <K extends keyof UserPreferences>(
        key: K,
        value: UserPreferences[K]
    ) => {
        if (!uid) return;
        await set(ref(database, `users/${uid}/preferences/${key}`), value);
    }, [uid]);

    return (
        <UserPreferencesContext.Provider value={{ preferences, canEdit: Boolean(uid), updatePreference }}>
            {children}
        </UserPreferencesContext.Provider>
    );
};

export const useUserPreferences = () => useContext(UserPreferencesContext);
